// GRW-35 item 7: "Add to this pursuit". Writes a pursuit link row through the
// link RPC of the chosen role, never a tenant-wide graph node; the projector
// draws the node from the registry (REG-53 / REG-57).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraSegmentedControl, SpecraSelect, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RegistryEntityPicker } from '../../components/SharedRecord/RegistryEntityPicker';
import { PURSUIT_GRAPH_KEY } from './graphHooks';
import { useCaptureWorkspace, useUpdateCaptureProject, useUpsertCaptureProfile, useUpsertCaptureTeamingLink, useUpsertCompetitiveFieldParticipant } from './hooks';
import { useUpsertPursuitStakeholder } from './themeHooks';
const ROLE_KIND = {
    stakeholder: 'CONTACT',
    competitor: 'COMPANY',
    teaming: 'COMPANY',
    customer: 'GOVERNMENT_ORG',
    vehicle: 'VEHICLE'
};
const AFFILIATIONS = [
    {
        value: 'customer',
        label: 'Customer'
    },
    {
        value: 'internal',
        label: 'Internal'
    },
    {
        value: 'partner',
        label: 'Partner'
    },
    {
        value: 'unknown',
        label: 'Unknown'
    }
];
export function PursuitGraphAddDialog({ open, onOpenChange, captureProjectId, hasCustomer, hasVehicle }) {
    const queryClient = useQueryClient();
    const [role, setRole] = useState('stakeholder');
    const [picked, setPicked] = useState(null);
    const [pursuitRole, setPursuitRole] = useState('');
    const [affiliation, setAffiliation] = useState('customer');
    const stakeholder = useUpsertPursuitStakeholder();
    const competitor = useUpsertCompetitiveFieldParticipant();
    const teaming = useUpsertCaptureTeamingLink();
    const customer = useUpdateCaptureProject();
    const vehicle = useUpsertCaptureProfile();
    // UpdateCaptureProject and UpsertCaptureProfile replace the whole row: carry the
    // stored values through so adding a customer or vehicle changes only that field.
    const workspace = useCaptureWorkspace(captureProjectId);
    const summary = workspace.data?.summary;
    const profile = workspace.data?.profile;
    const pending = stakeholder.isPending || competitor.isPending || teaming.isPending || customer.isPending || vehicle.isPending;
    function reset() {
        setPicked(null);
        setPursuitRole('');
        setAffiliation('customer');
    }
    function done() {
        void queryClient.invalidateQueries({
            queryKey: [
                PURSUIT_GRAPH_KEY
            ]
        });
        void queryClient.invalidateQueries({
            queryKey: [
                'pursuit-header'
            ]
        });
        void queryClient.invalidateQueries({
            queryKey: [
                'linked-nodes'
            ]
        });
        reset();
        onOpenChange(false);
    }
    function submit() {
        if (!picked) return;
        const id = picked.registryId;
        switch(role){
            case 'stakeholder':
                stakeholder.mutate({
                    captureProjectId,
                    contactId: id,
                    affiliation,
                    pursuitRole: pursuitRole.trim()
                }, {
                    onSuccess: done
                });
                return;
            case 'competitor':
                competitor.mutate({
                    captureProjectId,
                    companyProfileId: id,
                    inclusionBasis: 'market_signal'
                }, {
                    onSuccess: done
                });
                return;
            case 'teaming':
                teaming.mutate({
                    captureProjectId,
                    companyProfileId: id
                }, {
                    onSuccess: done
                });
                return;
            case 'customer':
                if (!summary) return;
                customer.mutate({
                    captureProjectId,
                    name: summary.name,
                    businessPhase: summary.businessPhase,
                    opportunityHypothesis: summary.opportunityHypothesis,
                    accountId: summary.accountId,
                    customerOrgId: id
                }, {
                    onSuccess: done
                });
                return;
            case 'vehicle':
                vehicle.mutate({
                    captureProjectId,
                    id: profile?.id,
                    solicitationNumber: profile?.solicitationNumber,
                    crmNumber: profile?.crmNumber,
                    businessDeveloperId: profile?.businessDeveloperId,
                    captureManagerId: profile?.captureManagerId,
                    customerAgencyNames: profile?.customerAgencyNames,
                    bidPosition: profile?.bidPosition,
                    vehicle: profile?.vehicle,
                    procurementType: profile?.procurementType,
                    contractType: profile?.contractType,
                    periodOfPerformance: profile?.periodOfPerformance,
                    totalProgramValue: profile?.totalProgramValue,
                    stageLabel: profile?.stageLabel,
                    pwinPercent: profile?.pwinPercent,
                    risks: profile?.risks,
                    vehicleId: id
                }, {
                    onSuccess: done
                });
                return;
        }
    }
    const options = [
        {
            value: 'stakeholder',
            label: 'Stakeholder'
        },
        {
            value: 'competitor',
            label: 'Competitor'
        },
        {
            value: 'teaming',
            label: 'Teaming partner'
        },
        {
            value: 'customer',
            label: 'Customer',
            disabled: hasCustomer
        },
        {
            value: 'vehicle',
            label: 'Vehicle',
            disabled: hasVehicle
        }
    ];
    const lockedHint = hasCustomer || hasVehicle ? `This pursuit already has a ${[
        hasCustomer && 'customer',
        hasVehicle && 'vehicle'
    ].filter(Boolean).join(' and ')}. Change it in Profile & plan.` : null;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: (next)=>{
            if (!next) reset();
            onOpenChange(next);
        },
        title: "Add to this pursuit",
        testId: Testids.captureGraph.addDialog,
        footerProps: {
            confirmLabel: 'Add',
            cancelLabel: 'Cancel',
            confirmDisabled: !picked || (role === 'customer' || role === 'vehicle') && !workspace.data,
            loading: pending,
            loadingLabel: 'Adding…',
            onConfirm: submit,
            onCancel: ()=>{
                reset();
                onOpenChange(false);
            },
            confirmProps: {
                'data-testid': Testids.captureGraph.addConfirm
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "As",
                    children: lockedHint ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: lockedHint,
                        children: /*#__PURE__*/ _jsx("span", {
                            children: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                label: "As",
                                value: role,
                                options: options,
                                testId: Testids.captureGraph.addAsSegment,
                                onValueChange: (v)=>{
                                    setRole(v);
                                    setPicked(null);
                                }
                            })
                        })
                    }) : /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "As",
                        value: role,
                        options: options,
                        testId: Testids.captureGraph.addAsSegment,
                        onValueChange: (v)=>{
                            setRole(v);
                            setPicked(null);
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Who",
                    children: /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                        kinds: [
                            ROLE_KIND[role]
                        ],
                        label: "Who",
                        value: picked?.registryId,
                        onPick: setPicked,
                        context: {
                            kind: 'PURSUIT',
                            id: captureProjectId
                        },
                        createMode: role === 'customer' ? 'none' : 'resolve'
                    }, role)
                }),
                role === 'stakeholder' && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Role on this pursuit",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: pursuitRole,
                                onChange: (e)=>setPursuitRole(e.target.value),
                                placeholder: "e.g. Contracting officer"
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Affiliation",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                value: affiliation,
                                onChange: (e)=>setAffiliation(e.target.value),
                                children: AFFILIATIONS.map((a)=>/*#__PURE__*/ _jsx("option", {
                                        value: a.value,
                                        children: a.label
                                    }, a.value))
                            })
                        })
                    ]
                })
            ]
        })
    });
}
